import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, apiContratos, type Contrato, type Feriado, type FormaPagamento, type Intervalo } from '../lib/api.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { usarFormulario } from '../lib/formulario.ts';
import { paraTela } from '../lib/datas.ts';
import { completarReais, digitarReais, paraApi } from '../lib/dinheiro.ts';
import { NIVEIS } from '../lib/rotulos.ts';
import { Confirmar } from './Confirmar.tsx';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { AjudaDoPasso, CampoDoPasso } from './PassoAPasso.tsx';
import { Valor } from './Valor.tsx';
import '../estilos/dinheiro-2026.css';

const FORMAS: Record<FormaPagamento, string> = {
  a_vista: 'À vista',
  entrada_parcelas: 'Entrada + parcelas',
  parcelado: 'Parcelado',
};

const INTERVALOS: Record<Intervalo, string> = {
  mensal: 'Todo mês, no mesmo dia',
  quinzenal: 'A cada 15 dias',
  dias_30: 'A cada 30 dias',
};

/**
 * O cliente aceitou: a proposta vira contrato e as cobranças nascem agora.
 *
 * ⚠️ **É aqui que a parcela nasce, e não antes** — decisão do Richard,
 * 15/08/2026. Enquanto era proposta, marcar vencimento seria inventar data de
 * um negócio que talvez não fechasse; e essa data apareceria no Financeiro
 * como dinheiro previsto que não existe.
 *
 * O valor não se edita aqui. Mudar preço no aceite seria fechar por um número
 * diferente do que o cliente aprovou — se o preço mudou, a proposta mudou, e
 * isso se corrige antes, no botão Editar.
 *
 * ⭐ **No desenho novo desde 18/09/2026** (a ficha em blocos, `docs/FORMULARIOS-
 * E-LINK-DO-CLIENTE.md`, A.4), com a **prévia das cobranças** antes de gerar e a
 * pergunta antes de aceitar. O que se envia é o mesmo, pela mesma rota.
 */
export function AceitarProposta({
  contrato,
  aoFechar,
  aoAceitar,
}: {
  contrato: Contrato;
  aoFechar: () => void;
  aoAceitar: () => Promise<void>;
}) {
  const [assinadoEm, setAssinado] = useState('');
  const [primeiroVencimento, setPrimeiro] = useState('');
  const [forma, setForma] = useState<FormaPagamento>('entrada_parcelas');
  const [entrada, setEntrada] = useState('');
  const [parcelas, setParcelas] = useState('2');
  const [intervalo, setIntervalo] = useState<Intervalo>('mensal');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const emVoo = useRef(false);

  const guarda = usarFormulario({ assinadoEm, primeiroVencimento, forma, entrada, parcelas, intervalo }, aoFechar);
  usarDialogo(guarda.fechar);

  /* Os feriados da janela das cobranças, para a prévia adiar como o servidor adia. */
  const n = forma === 'a_vista' ? 1 : Number(parcelas) || 1;
  useEffect(() => {
    if (!primeiroVencimento) return;
    let valeu = true;
    api
      .feriados(primeiroVencimento, somarDias(primeiroVencimento, (n + 2) * 31))
      .then((r) => valeu && setFeriados(r.feriados))
      .catch(() => undefined);
    return () => {
      valeu = false;
    };
  }, [primeiroVencimento, n]);

  const previa = useMemo(
    () =>
      primeiroVencimento
        ? montarPrevia({ total: contrato.valorTotal ?? '0', forma, entrada: forma === 'entrada_parcelas' ? paraApi(entrada) : null, parcelas: n, primeiroVencimento, intervalo, feriados })
        : [],
    [contrato.valorTotal, forma, entrada, n, primeiroVencimento, intervalo, feriados],
  );

  /* ⭐ 29/09/2026: a cortesia se aceita como a paga, mas sem cobrança de honorário. */
  const cortesia = contrato.cortesia;
  function problema(): string | null {
    if (cortesia) return null;
    if (!primeiroVencimento) return 'Informe o vencimento da primeira cobrança.';
    if (forma === 'entrada_parcelas' && !paraApi(entrada)) return 'Informe o valor da entrada.';
    return null;
  }
  function pedirParaAceitar() {
    if (emVoo.current) return;
    const x = problema();
    if (x) return setErro(x);
    setErro(null);
    setConfirmando(true);
  }

  async function aceitar() {
    if (emVoo.current) return;
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await apiContratos.aceitar(
        contrato.id,
        cortesia
          ? { assinadoEm: assinadoEm || null }
          : {
              assinadoEm: assinadoEm || null,
              primeiroVencimento,
              formaPagamento: forma,
              entrada: forma === 'entrada_parcelas' ? paraApi(entrada) : null,
              parcelas: forma === 'a_vista' ? undefined : Number(parcelas) || 1,
              intervalo,
            },
      );
      guarda.marcarComoSalvo({ assinadoEm, primeiroVencimento, forma, entrada, parcelas, intervalo });
      await aoAceitar();
      aoFechar();
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Não consegui aceitar a proposta.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  const mexe =
    <T,>(f: (v: T) => void) =>
    (v: T) => {
      setErro(null);
      f(v);
    };
  const somaDaPrevia = previa.reduce((s, p) => s + p.centavos, 0);

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && guarda.fechar()}>
      <div className="imv cad-conferir din-ficha din-estreita" role="dialog" aria-modal="true" aria-label="Aceitar proposta">
        <section className="imv-ficha">
          <header className="imv-ficha-topo">
            <div>
              <span className="s-sobrelinha">Ficha do cliente · aceitar proposta</span>
              <h3 className="cad-titulo">
                Aceitar a proposta <span className="pap-titulo-codigo">{contrato.codigo}</span>
              </h3>
              <p className="cad-sub din-sub">
                {contrato.niveisDoContrato.map((x) => (
                  <span key={x.nivel} className={`selo-nivel selo-${x.nivel}`}>
                    {NIVEIS[x.nivel].longo}
                  </span>
                ))}
                <span>
                  {cortesia ? 'honorários em cortesia' : <>honorários de <Valor de={contrato.valorTotal} /></>}
                  {contrato.fundoDeCustas && (
                    <>
                      {' '}
                      e Fundo de Custas de <Valor de={contrato.fundoDeCustas} />
                    </>
                  )}
                </span>
              </p>
            </div>
            <button type="button" className="imv-fechar" aria-label="Fechar" onClick={guarda.fechar}>
              <Icone nome="fechar" tamanho={16} />
            </button>
          </header>

          <div className="imv-corpo">
            <div className="imv-conteudo">
              <Bloco titulo="A assinatura">
                <div className="imv-grade-campos">
                  <CampoDoPasso rotulo="Assinado em" col={2} mono nota="É desta data que o prazo do caso passa a contar.">
                    <input id="ac-assinado" type="date" value={assinadoEm} onChange={(e) => mexe(setAssinado)(e.target.value)} />
                  </CampoDoPasso>
                </div>
              </Bloco>

              {cortesia ? (
                <Bloco titulo="Os honorários">
                  <p className="imv-nada">
                    São cortesia: nenhuma cobrança de honorário nasce no aceite. O Fundo de Custas segue o rito normal e se lança no caso, na aba do fundo, quando o
                    cliente depositar.
                  </p>
                </Bloco>
              ) : (
              <>
              <Bloco titulo="Como o cliente paga">
                <div className="imv-seg din-seg" role="radiogroup" aria-label="Forma de pagamento">
                  {(Object.keys(FORMAS) as FormaPagamento[]).map((f) => (
                    <button key={f} type="button" role="radio" aria-checked={forma === f} className={`imv-op${forma === f ? ' on' : ''}`} onClick={() => mexe(setForma)(f)}>
                      <i />
                      {FORMAS[f]}
                    </button>
                  ))}
                </div>
                <div className="imv-grade-campos din-campos">
                  <CampoDoPasso rotulo={forma === 'a_vista' ? 'Vence em' : 'Primeira cobrança vence em'} obrigatorio col={2} mono>
                    <input id="ac-venc" type="date" value={primeiroVencimento} onChange={(e) => mexe(setPrimeiro)(e.target.value)} />
                  </CampoDoPasso>
                  {forma === 'entrada_parcelas' && (
                    <CampoDoPasso rotulo="Entrada" obrigatorio col={2} mono nota="Vence na data da primeira cobrança.">
                      <i className="imv-pre">R$</i>
                      <input id="ac-entrada" inputMode="decimal" value={entrada} onChange={(e) => mexe(setEntrada)(digitarReais(e.target.value))} onBlur={(e) => setEntrada(completarReais(e.target.value))} placeholder="0,00" />
                    </CampoDoPasso>
                  )}
                  {forma !== 'a_vista' && (
                    <>
                      <CampoDoPasso rotulo="Em quantas parcelas" col={2} mono>
                        <input id="ac-parcelas" inputMode="numeric" value={parcelas} onChange={(e) => mexe(setParcelas)(e.target.value.replace(/\D/g, '').slice(0, 2))} />
                      </CampoDoPasso>
                      <CampoDoPasso rotulo="De quanto em quanto tempo" col={3}>
                        <select id="ac-intervalo" value={intervalo} onChange={(e) => mexe(setIntervalo)(e.target.value as Intervalo)}>
                          {(Object.keys(INTERVALOS) as Intervalo[]).map((i) => (
                            <option key={i} value={i}>
                              {INTERVALOS[i]}
                            </option>
                          ))}
                        </select>
                      </CampoDoPasso>
                    </>
                  )}
                </div>
              </Bloco>

              <Bloco titulo="As cobranças que vão nascer">
                {previa.length === 0 ? (
                  <p className="imv-nada">Com o vencimento da primeira cobrança, a prévia aparece aqui.</p>
                ) : (
                  <>
                    <div className="imv-tabela din-tabela">
                      <div className="imv-tl imv-tl-cab din-grade">
                        <span>Cobrança</span>
                        <span>Vencimento</span>
                        <span>Valor</span>
                      </div>
                      {previa.map((p) => (
                        <div key={p.numero} className="imv-tl din-grade">
                          <span>{p.numero === 0 ? 'Entrada' : `Parcela ${p.numero}`}</span>
                          <span className="mono">
                            {paraTela(p.vencimentoEm)}
                            {p.adiada && <small className="din-adiada"> · adiada para o dia útil</small>}
                          </span>
                          <span className="mono">
                            <Valor de={(p.centavos / 100).toFixed(2)} />
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className={`din-soma${somaDaPrevia === centavosDe(contrato.valorTotal) ? ' fecha' : ''}`}>
                      Somam <Valor de={(somaDaPrevia / 100).toFixed(2)} />
                      {somaDaPrevia === centavosDe(contrato.valorTotal) ? ' — fecha com os honorários.' : ' — não fecha com os honorários: confira a entrada.'}
                    </p>
                  </>
                )}
              </Bloco>
              </>
              )}
            </div>
            <AjudaDoPasso titulo="Ao aceitar">
              {cortesia ? (
                <>
                  <p>
                    A proposta vira <b>contrato ativo</b>. Os honorários são cortesia e não geram cobrança; a data da assinatura é de onde o prazo do caso conta.
                  </p>
                  <p>O Fundo de Custas segue o rito normal: o depósito do cliente se lança no caso, e a prestação de contas sai de lá.</p>
                </>
              ) : (
                <>
                  <p>
                    A proposta vira <b>contrato ativo</b> e as cobranças nascem agora, como na prévia. O valor não se muda aqui: se mudou, a proposta mudou — corrija
                    no Editar.
                  </p>
                  <p>Vencimento em dia não útil vai para o próximo dia útil. Depois, datas e valores se corrigem em Corrigir cobranças.</p>
                </>
              )}
            </AjudaDoPasso>
          </div>

          {erro && <p className="erro imv-erro">{erro}</p>}

          <footer className="imv-ficha-pe">
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <span className="imv-esp" />
            <button type="button" className="imv-bt primario" disabled={salvando} onClick={pedirParaAceitar}>
              <Icone nome="check" tamanho={14} /> {salvando ? 'Aceitando…' : cortesia ? 'Aceitar' : 'Aceitar e gerar cobranças'}
            </button>
          </footer>
        </section>
      </div>

      {confirmando && (
        <Confirmar
          titulo="Aceitar a proposta"
          descricao={
            cortesia
              ? `A proposta ${contrato.codigo} vira contrato ativo. Os honorários são cortesia: nenhuma cobrança nasce.`
              : `A proposta ${contrato.codigo} vira contrato ativo, e ${previa.length === 1 ? 'a cobrança nasce' : `as ${previa.length} cobranças nascem`} agora.`
          }
          rotuloAcao="Sim, aceitar"
          tom="primario"
          aoFechar={() => setConfirmando(false)}
          aoConfirmar={() => void aceitar()}
        />
      )}
      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue={`o aceite da proposta ${contrato.codigo}`}
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => undefined}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </div>
  );
}

function Bloco({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="din-bloco">
      <div className="cad-subtitulo">
        <h5>{titulo}</h5>
      </div>
      {children}
    </section>
  );
}

/* ═══ A prévia — espelho de `montarParcelas` (`api/src/rotas/contratos.ts`) ═══
 *
 * ⚠️ **Quem decide é o servidor**; a prévia só mostra antes o que ele vai gerar.
 * As regras são as dele, e por isso ficam escritas iguais: o centavo que a
 * divisão sobra vai todo na primeira parcela; a data conta **sempre da base**
 * (nunca da anterior); e o que cai em fim de semana ou feriado **adia** para o
 * próximo dia útil (feriado facultativo não fecha o dia). Divergindo dele, a
 * correção é aqui — e a regressão da tela confere os números. */

function somarDias(data: string, dias: number): string {
  const d = new Date(`${data}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}
function somarMeses(data: string, meses: number): string {
  const d = new Date(`${data}T00:00:00Z`);
  const dia = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + meses);
  const ultimo = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(dia, ultimo));
  return d.toISOString().slice(0, 10);
}
const centavosDe = (valor: string | null | undefined) => Math.round(Number(valor ?? '0') * 100);

function montarPrevia(d: {
  total: string;
  forma: FormaPagamento;
  entrada: string | null;
  parcelas: number;
  primeiroVencimento: string;
  intervalo: Intervalo;
  feriados: Feriado[];
}): Array<{ numero: number; centavos: number; vencimentoEm: string; adiada: boolean }> {
  const fecha = new Set(d.feriados.filter((f) => f.tipo !== 'facultativo').map((f) => f.data));
  const util = (data: string) => {
    const dia = new Date(`${data}T00:00:00Z`).getUTCDay();
    return dia !== 0 && dia !== 6 && !fecha.has(data);
  };
  const vencer = (passos: number) => {
    const bruta = d.intervalo === 'mensal' ? somarMeses(d.primeiroVencimento, passos) : somarDias(d.primeiroVencimento, (d.intervalo === 'quinzenal' ? 15 : 30) * passos);
    let atual = bruta;
    for (let i = 0; i < 10 && !util(atual); i += 1) atual = somarDias(atual, 1);
    return { vencimentoEm: atual, adiada: atual !== bruta };
  };
  const total = centavosDe(d.total);
  if (d.forma === 'a_vista') return [{ numero: 1, centavos: total, ...vencer(0) }];
  const lista: Array<{ numero: number; centavos: number; vencimentoEm: string; adiada: boolean }> = [];
  let restante = total;
  let passoInicial = 0;
  if (d.forma === 'entrada_parcelas' && d.entrada) {
    const entrada = centavosDe(d.entrada);
    lista.push({ numero: 0, centavos: entrada, ...vencer(0) });
    restante = total - entrada;
    passoInicial = 1;
  }
  /* ROUND_DOWN do servidor corta em direção ao zero: `trunc`, e não `floor`. */
  const base = Math.trunc(restante / d.parcelas);
  const sobra = restante - base * d.parcelas;
  for (let i = 1; i <= d.parcelas; i += 1) {
    lista.push({ numero: i, centavos: i === 1 ? base + sobra : base, ...vencer(passoInicial + i - 1) });
  }
  return lista;
}
